import Axios from 'configuredAxios';
import isNumber from 'lodash/isNumber';
import { datadogRum } from '@datadog/browser-rum';
import { Honeybadger } from '@honeybadger-io/react';

import { isKioskDevice } from 'lib/helpers/kiosk';
import { getSafeRequestUrl, toHoneybadgerError } from 'lib/helpers/honeybadger';

let hasActiveSession = false;

export const setActiveSession = (value) => {
  hasActiveSession = value;
};

const NETWORK_ERROR_CODES = ['ERR_NETWORK', 'ECONNABORTED', 'ERR_CANCELED'];

const isNetworkLevelError = (error) => {
  if (!error) return false;
  const message = typeof error.message === 'string' ? error.message : '';
  return (
    NETWORK_ERROR_CODES.includes(error.code) ||
    error.__CANCEL__ === true ||
    message === 'Network Error' ||
    /timeout.*exceeded/i.test(message)
  );
};

export const requestApi = async ({
  url,
  params,
  onSuccess = () => {},
  onError = () => {},
  onCatch = () => {},
  navigate = () => {},
  filePath = '',
  throwError = false,
  method = 'post',
  returnRaw = false,
  signal = null, // for abortion requests
  responseType = 'json',
  suppressAuthRedirect = false,
  shouldHandleRedirect = true,
  reportError = true,
  timeout = 0
}) => {
  const honeyBadgerNotify = (error) => {
    const safeUrl = getSafeRequestUrl(url);
    Honeybadger.notify(toHoneybadgerError(error), `[error]requestApi:${safeUrl}`, {
      tags: `error,frontend,${filePath}`,
      context: {
        requestUrl: safeUrl,
        errorCode: error?.code,
        responseStatus: error?.response?.status
      }
    });
  };

  try {
    const res = await Axios.request({ method, url, data: params, signal, responseType, timeout });
    const path = isKioskDevice()
      ? '/kiosk/idle'
      : window.location.pathname.includes('/admin')
        ? '/admin/login'
        : '/login';

    localStorage.setItem('last_active', new Date().valueOf().toString());

    if (responseType === 'blob' && res.data instanceof Blob) {
      try {
        // Convert blob to text and parse JSON
        const resData = await res.data.text();
        const data = JSON.parse(resData);
        res.data = data;
      } catch {
        if (onSuccess) onSuccess(res.data);
        return res.data;
      }
    }

    const { redirect, error, code, ...data } = res.data;

    switch (code) {
      case -1:
        if (!shouldHandleRedirect) break;
        datadogRum.addAction('session-redirect', {
          redirect,
          path,
          url: typeof url === 'string' ? url.split('?')[0] : '',
          code: -1
        });
        if (!suppressAuthRedirect) {
          if (redirect === '/' || !redirect) {
            navigate(path, hasActiveSession ? { state: { idle: true } } : undefined);
            hasActiveSession = false;
          } else {
            navigate(redirect);
          }
        }
        break;
      case 0:
        onSuccess(data, res.data, redirect);
        break;
      case 1:
        onError(error, code, redirect);
        if (!signal && reportError) honeyBadgerNotify(error);
        break;
      case 2:
      case 3:
      case 4:
      case 5:
        onError(error, code, redirect);
        break;
      default:
        onError(error, code, res.data);
        // numeric custom codes are handled business outcomes, not frontend exceptions
        if (!signal && reportError && !isNumber(code)) honeyBadgerNotify(error);
        break;
    }
    if (code !== 0 && throwError) throw { error, code };
    if (returnRaw) {
      return res;
    } else {
      return res.data;
    }
  } catch (error) {
    if (throwError) {
      throw error;
    }
    onCatch(error);
    if (!signal) {
      const isNetworkError = isNetworkLevelError(error);
      if (isNetworkError) {
        console.warn(`Network request failed: ${url}`, error);
        datadogRum.addAction('network_request_failed', { url, code: error?.code });
      } else {
        console.error(error);
        if (reportError && !isNumber(error?.code)) honeyBadgerNotify(error);
      }
    }
    return { data: {}, headers: {}, status: 403 };
  }
};
